import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";
import { API } from "@/constants";

export default function VideoStories() {
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios.get(`${API}/videos`).then((r) => setVideos(r.data.videos || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const stories = videos.filter((v) => v.video_type === "story");
  const standard = videos.filter((v) => v.video_type !== "story");

  if (!videos.length) return null;

  return (
    <section id="videos" className="bg-black py-20 lg:py-28 overflow-hidden" data-testid="video-stories-section">
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        {/* KERA STORIES */}
        {stories.length > 0 && (
          <>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="font-mono text-xs uppercase tracking-[0.25em] text-kera-gold mb-4"
            >
              KERA Stories
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.08 }}
              className="font-display font-bold text-white text-2xl sm:text-3xl lg:text-4xl mb-10"
            >
              Moments from the journey
            </motion.h2>
            <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x" data-testid="stories-row">
              {stories.map((v, i) => (
                <motion.button
                  key={v.id}
                  onClick={() => setActive(v)}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.07 }}
                  className="group relative shrink-0 w-[170px] sm:w-[200px] aspect-[9/16] rounded-2xl overflow-hidden border border-kera-gold/25 snap-start text-left"
                  data-testid={`story-card-${v.video_id}`}
                >
                  <img src={v.thumbnail} alt={v.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-black/25" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="h-12 w-12 rounded-full bg-kera-gold/90 text-black flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                      <Play size={18} fill="currentColor" />
                    </span>
                  </span>
                  <span className="absolute bottom-0 inset-x-0 p-4">
                    <span className="block font-display font-semibold text-white text-sm leading-snug">{v.title}</span>
                  </span>
                </motion.button>
              ))}
            </div>
          </>
        )}

        {/* WATCH KERA */}
        {standard.length > 0 && (
          <div className="mt-16 lg:mt-20">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="font-mono text-xs uppercase tracking-[0.25em] text-kera-gold mb-4"
            >
              Watch KERA
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.08 }}
              className="font-display font-bold text-white text-2xl sm:text-3xl lg:text-4xl mb-10"
            >
              Films &amp; features
            </motion.h2>
            <div
              className="flex sm:grid sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 overflow-x-auto sm:overflow-visible pb-4 sm:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x"
              data-testid="watch-grid"
            >
              {standard.map((v, i) => (
                <motion.button
                  key={v.id}
                  onClick={() => setActive(v)}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: (i % 4) * 0.06 }}
                  className="group shrink-0 w-[74vw] sm:w-auto snap-start text-left"
                  data-testid={`watch-card-${v.video_id}`}
                >
                  <div className="relative aspect-video rounded-xl overflow-hidden border border-white/10">
                    <img src={v.thumbnail} alt={v.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors duration-300" />
                    <span className="absolute inset-0 flex items-center justify-center opacity-90 group-hover:opacity-100 transition-opacity">
                      <span className="h-11 w-11 rounded-full bg-white/90 text-black flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                        <Play size={16} fill="currentColor" />
                      </span>
                    </span>
                  </div>
                  <p className="mt-3 font-body font-semibold text-sm text-white/85 group-hover:text-kera-gold transition-colors leading-snug">
                    {v.title}
                  </p>
                  {v.publish_date && (
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-white/35">{v.publish_date}</p>
                  )}
                </motion.button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Video modal — player loads only on open */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setActive(null)}
            data-testid="video-modal"
          >
            <button
              onClick={() => setActive(null)}
              className="absolute top-5 right-5 h-11 w-11 rounded-full border border-white/25 text-white flex items-center justify-center hover:border-kera-gold hover:text-kera-gold transition-colors"
              aria-label="Close video"
              data-testid="video-modal-close"
            >
              <X size={18} />
            </button>
            <motion.div
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 12 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className={`w-full ${active.video_type === "story" ? "max-w-sm aspect-[9/16] max-h-[85vh]" : "max-w-4xl aspect-video"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <iframe
                src={`https://www.youtube.com/embed/${active.video_id}?rel=0`}
                title={active.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full rounded-2xl border border-kera-gold/30"
                data-testid="video-modal-player"
              />
              <p className="mt-4 text-center font-display font-semibold text-white/85">{active.title}</p>
              <p className="mt-2 text-center">
                <a
                  href={`https://www.youtube.com/watch?v=${active.video_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-[10px] uppercase tracking-[0.2em] text-kera-gold/80 hover:text-kera-gold transition-colors border-b border-kera-gold/40 pb-0.5"
                  data-testid="video-watch-on-youtube"
                >
                  Not playing? Watch on YouTube
                </a>
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
